import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowDrilldown", "allowSort", "drilldownEventCallback", "initialDrilldownKeys", "report", "onReportResolve", "onError", "options", "applyMigrateReport", "onExportListClick", "onExportListSubmit", "onCustomExportButtonClick", "hideExportButton", "customSaveReportButtonProps", "customExportReportButtonProps", "drilldownAlert", "drilldownDescription", "goToCrmLink", "overrideTooltip", "useInlineLabels", "isMobile", "mobileReportingOptions", "runtimeOptions", "trackingApplicationName", "trackingAssetType", "trackingScreenName", "onReportChange", "wrapperComponent", "customSubheader", "inlineLabelTypes", "enableReportResolveUsageTracking", "disableUnifiedVizValidation", "disableZoom"],
  _excluded2 = ["lazyReportResolve", "PlatformResolveComponent", "report", "onLazyLoadingDeferredReportResolve"],
  _excluded3 = ["userInfo", "fiscalYearMonthName", "report"];
/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateDrilldownReport } from 'reporting-data/config/drilldown/generate';
import { logError } from 'reporting-data/monitoring/logger';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { IntersectionObserver } from 'foundations-components/transitional/components/IntersectionObserver';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
// @ts-expect-error Untyped dependency
import mapOptionsToResolverProps from '../customWidgets/lib/mapOptionsToResolverProps';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { isBaseReport, isConfigReport, isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { EVENTS } from '../../lib/events';
import { getReportDrillIntoUsageTrackerProperties, getDefaultReportRenderProperties } from '../../lib/usageTrackerUtils';
// @ts-expect-error Untyped dependency
import { DRILLDOWN_CLOSED, DRILLDOWN_OPENED } from './DrilldownEvents';
// @ts-expect-error Untyped dependency
import { DrilldownViewer } from './DrilldownViewer';
// @ts-expect-error Untyped dependency
import ReportContent from './ReportContent';
import { useEnhancedReport } from './useEnhancedReport';
import { UserInfoProvider } from './UserInfoProvider';
// @ts-expect-error Untyped dependency
import ReportErrored from '../../reports/inferLogic/components/states/ReportErrored';
import FiscalYearProvider from './FiscalYearProvider';
import { List as ImmutableList } from 'immutable';
import ReportingPluginsProvider from 'reporting-plugins/ReportingPluginsProvider';
import { Metrics } from '../../monitoring/Metrics';
import { getUsageTracker } from '../../monitoring/usageTracker';
import { ErrorBoundary } from '../../reports/decorators/ErrorBoundaryDecorator';
import ReportSettingsLabel from '../inlineLabels/ReportSettingsLabel';
import { useStandardizedReport } from './useStandardizedReport';
import ReportContentUnifiedViz from './v2/ReportContent';
import { useIsUnifiedReportVizEnabled } from './v2/useIsUnifiedReportVizEnabled';
import { MaybeUnifiedVizValidationContextProvider } from './v2/UnifiedVizValidationContextProvider';
import { useReportingLogger, ReportingLoggerProvider } from './ReportingLoggerProvider';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;

/**
 * Props interface for PlatformReportResolver Component
 */

function RenderPlatformReportResolve(_ref) {
  let {
      allowDrilldown = true,
      allowSort = true,
      drilldownEventCallback = noop,
      initialDrilldownKeys = ImmutableList(),
      report,
      onReportResolve = noop,
      onError,
      options = {},
      applyMigrateReport = true,
      onExportListClick,
      onExportListSubmit,
      onCustomExportButtonClick,
      hideExportButton = false,
      customSaveReportButtonProps,
      customExportReportButtonProps,
      drilldownAlert,
      drilldownDescription,
      goToCrmLink,
      overrideTooltip,
      useInlineLabels = true,
      isMobile = false,
      mobileReportingOptions = {},
      runtimeOptions = {},
      trackingApplicationName,
      trackingAssetType,
      trackingScreenName,
      onReportChange,
      wrapperComponent,
      customSubheader,
      inlineLabelTypes,
      enableReportResolveUsageTracking = true,
      disableUnifiedVizValidation = false,
      disableZoom = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [drilled, setDrilled] = useState(null);
  const [reportResolveData, setReportResolveData] = useState();
  const reportResolveTimeoutRef = useRef(null);
  const {
    loggerRef
  } = useReportingLogger();
  const initialReport = useRef(report);
  const reportingLogger = useMemo(() => {
    const rl = new ReportingLogger();
    loggerRef.current.platformReportResolveLogger = rl;
    if (initialReport.current) {
      rl.addReportAttributes(initialReport.current);
    }
    return rl;
  }, []);
  useEffect(() => {
    Metrics.counter('render-report', {
      wrapperComponent: wrapperComponent || 'unknown',
      applicationName: trackingApplicationName || 'unknown'
    }).increment();
  }, [wrapperComponent, trackingApplicationName]);
  useEffect(() => {
    return () => {
      if (reportResolveTimeoutRef.current) {
        clearTimeout(reportResolveTimeoutRef.current);
      }
    };
  }, []);
  const usageTracker = getUsageTracker();
  const onDrilldown = useCallback(allowDrilldown ? drilldownReport => {
    usageTracker.track(EVENTS.drillInto, getReportDrillIntoUsageTrackerProperties(report, trackingAssetType, trackingApplicationName, trackingScreenName));
    drilldownEventCallback(DRILLDOWN_OPENED, {
      report: drilldownReport
    });
    setDrilled(drilldownReport);
    reportingLogger.sendPageAction('PlatformReportResolverDrilldown');
  } : noop, [drilldownEventCallback, usageTracker, report]);
  useEffect(() => {
    if (initialDrilldownKeys && initialDrilldownKeys.size !== 0) {
      generateDrilldownReport(report, initialDrilldownKeys).then(setDrilled);
    }
  }, [report, initialDrilldownKeys]);
  const asyncMigratedReport = useStandardizedReport({
    report,
    applyMigrateReport,
    runtimeOptions
  });

  // Send migration report to debug callback when available
  useEffect(() => {
    if (rest.debug && isSucceeded(asyncMigratedReport)) {
      const migratedReport = asyncMigratedReport.data;
      rest.debug.set('MigrationResult', {
        originalReport: report,
        migratedReport
      });
    }
  }, [rest.debug, asyncMigratedReport, report]);
  const drillableReport = useMemo(() => {
    if (isSucceeded(asyncMigratedReport)) {
      const {
        data: migratedReport
      } = asyncMigratedReport;
      const data = migratedReport && migratedReport.withMutations(context => {
        context.setIn(['displayParams', 'onDrilldown'], onDrilldown).setIn(['displayParams', 'allowDrilldown'], allowDrilldown);
      });
      return Object.assign({}, asyncMigratedReport, {
        data
      });
    }
    return asyncMigratedReport;
  }, [asyncMigratedReport]);
  const {
    visualize
  } = rest;
  const asyncEnhancedReport = useEnhancedReport(drillableReport, {
    allowDrilldown,
    allowSort,
    hasCustomVisualize: !!visualize
  }, onReportChange);
  useEffect(() => {
    reportingLogger.addAttribute('PlatformReportResolver', true);
  }, [reportingLogger]);
  const onReportSuccess = useCallback(args => {
    onReportResolve(args);
    setReportResolveData(args);

    /**
     * This is being added because in Hover Tracking, we're seeing cases
     * where the report doesn't have an ID on Dashboards, however almost
     * all successful report resolves on Dashboards have a report ID.
     * This added information will help us understand if a report loses
     * its ID between when it gets passed to PlatformReportResolver and
     * when it reaches ReportContent.
     */
    if (!isLoading(asyncEnhancedReport) && !isFailed(asyncEnhancedReport)) {
      const enhancedReport = asyncEnhancedReport.data;
      if (isBaseReport(enhancedReport)) {
        reportingLogger.addAttributes({
          enhancedReportId: enhancedReport.get('id')
        });
      }
      if (isConfigReport(enhancedReport)) {
        reportingLogger.addAttributes({
          enhancedReportChartType: enhancedReport.get('chartType'),
          enhancedReportConfig: enhancedReport.get('config').toJS(),
          enhancedReportConfigType: enhancedReport.getIn(['config', 'configType'])
        });
      }
      if (isRelationalReport(enhancedReport)) {
        reportingLogger.addAttributes({
          enhancedReportReportDefinition: enhancedReport.get('reportDefinition')
        });
      }
    }
    reportingLogger.logSuccess('PlatformReportResolve');

    // Send timeToResolve to debug callback when available
    const {
      timeToResolve,
      isAsyncResolve = false
    } = reportingLogger.getAttributes();
    if (rest.debug && timeToResolve) {
      rest.debug.set('ReportResolveTime', {
        timeToResolve: timeToResolve
      });
    }
    if (enableReportResolveUsageTracking) {
      const usageTrackerTimeoutId = setTimeout(() => {
        usageTracker.track(EVENTS.reportResolve, Object.assign({}, getDefaultReportRenderProperties(report, trackingAssetType, trackingApplicationName, trackingScreenName, true), {
          status: 'SUCCESS',
          runtime: timeToResolve,
          notSynced: isAsyncResolve
        }));
      }, 3000);

      // Store timeout ID for cleanup
      reportResolveTimeoutRef.current = usageTrackerTimeoutId;
    }
  }, [reportingLogger, onReportResolve, asyncEnhancedReport]);
  const onReportError = useCallback(error => {
    logError({
      error,
      tags: {
        component: 'PlatformReportResolve'
      },
      extra: {
        report
      }
    });
    reportingLogger.logError(error, 'PlatformReportResolve', 'PlatformReportResolve');
    if (enableReportResolveUsageTracking) {
      usageTracker.track(EVENTS.reportResolve, Object.assign({}, getDefaultReportRenderProperties(report, trackingAssetType, trackingApplicationName, trackingScreenName), {
        status: 'FAILURE',
        errorName: error.name,
        errorMessage: error.message
      }));
    }
    if (typeof onError === 'function') {
      onError(error);
    }
  }, [reportingLogger, onError]);
  const onDrilldownError = useCallback(error => {
    logError({
      error,
      tags: {
        component: 'PlatformReportResolve'
      },
      extra: {
        report
      }
    });
    reportingLogger.logError(error, 'PlatformReportDrilldown', 'PlatformReportResolve');
  }, [reportingLogger]);
  const shouldUseUnifiedViz = useIsUnifiedReportVizEnabled(report, runtimeOptions);
  if (isLoading(asyncEnhancedReport)) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true
    });
  }
  if (isFailed(asyncEnhancedReport)) {
    return /*#__PURE__*/_jsx(ReportErrored, {
      error: asyncEnhancedReport.error
    });
  }

  // Asserts updatedReport is not undefined as AsyncData.data is optional;
  const updatedReport = asyncEnhancedReport.data;
  const trackingProperties = {
    assetType: trackingAssetType,
    applicationName: trackingApplicationName,
    screen: trackingScreenName
  };
  const isCompletedActivitiesReport = getCustomWidgetType(report) === 'COMPLETED_ACTIVITIES';
  const shouldShowInlineLabels = useInlineLabels && !isCompletedActivitiesReport;
  const reportContentProps = Object.assign({}, mapOptionsToResolverProps(options), rest, {
    runtimeOptions,
    isMobile,
    mobileReportingOptions,
    report: updatedReport,
    onReportResolve: onReportSuccess,
    onDrilldown,
    onError: onReportError,
    allowSort,
    appUsageTracker: usageTracker,
    onReportChange,
    trackingAssetType,
    originalReport: report,
    disableZoom
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [report && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ReportSettingsLabel, {
        asyncEnhancedReport: asyncEnhancedReport,
        overrideTooltip: overrideTooltip,
        inlineLabelTypes: inlineLabelTypes,
        isMobile: isMobile,
        customSubheader: customSubheader,
        reportResolveData: reportResolveData,
        useInlineLabels: shouldShowInlineLabels
      }), shouldUseUnifiedViz ? /*#__PURE__*/_jsx(ReportContentUnifiedViz, Object.assign({}, reportContentProps)) : /*#__PURE__*/_jsx(MaybeUnifiedVizValidationContextProvider, {
        report: updatedReport,
        debug: reportContentProps.debug,
        isMobile: isMobile,
        disableUnifiedVizValidation: disableUnifiedVizValidation,
        children: /*#__PURE__*/_jsx(ReportContent, Object.assign({}, reportContentProps))
      })]
    }), drilled && /*#__PURE__*/_jsx(DrilldownViewer, {
      appUsageTracker: usageTracker,
      baseReport: asyncEnhancedReport.data,
      drilldownEventCallback: drilldownEventCallback,
      report: drilled.delete('id'),
      onClose: () => {
        setDrilled(null);
        drilldownEventCallback(DRILLDOWN_CLOSED);
      },
      onError: onDrilldownError,
      onExportListClick: onExportListClick,
      onExportListSubmit: onExportListSubmit,
      onCustomExportButtonClick: onCustomExportButtonClick,
      customSaveReportButtonProps: customSaveReportButtonProps,
      customExportReportButtonProps: customExportReportButtonProps,
      hideExportButton: hideExportButton,
      drilldownAlert: drilldownAlert,
      drilldownDescription: drilldownDescription,
      goToCrmLink: goToCrmLink,
      trackingProperties: trackingProperties
    })]
  });
}
const RenderPlatformReportResolveWithPlugins = ReportingPluginsProvider(RenderPlatformReportResolve);
function RenderMaybeWithLazyLoad(_ref2) {
  let {
      lazyReportResolve,
      PlatformResolveComponent,
      report,
      onLazyLoadingDeferredReportResolve = noop
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const shouldLazyLoad = lazyReportResolve === undefined ? true : lazyReportResolve;

  // Ensures that lazy loading only happens once and drilldowns work as the base report is no longer intersecting
  const [hasPreviouslyIntersected, setHasPreviouslyIntersected] = useState(false);
  // While this value should be the same as hasPreviouslyInteracted, this is used in cleanup, so we do not want to rely on state
  const reportViewed = useRef(false);
  const [reportingLogger] = useState(() => {
    const rl = new ReportingLogger();
    rl.addReportAttributes(report);
    return rl;
  });
  useEffect(() => {
    return () => {
      reportingLogger.addAttributes({
        lazyLoadViewed: reportViewed.current,
        reportLazyLoaded: shouldLazyLoad,
        lazyLoadParameter: lazyReportResolve
      });
      reportingLogger.sendPageAction('LazyLoadReports');
    };
  }, []);
  useEffect(() => {
    if (shouldLazyLoad && !hasPreviouslyIntersected) {
      onLazyLoadingDeferredReportResolve();
    }
  }, [shouldLazyLoad, hasPreviouslyIntersected]);
  if (shouldLazyLoad) {
    return /*#__PURE__*/_jsx(IntersectionObserver, {
      "data-test-id": "platform-report-resolver-lazy-load-intersection-observer",
      renderContent: isIntersecting => {
        return isIntersecting || hasPreviouslyIntersected ? /*#__PURE__*/_jsx(PlatformResolveComponent, Object.assign({
          report: report
        }, rest)) : /*#__PURE__*/_jsx("div", {});
      },
      onIntersectionChange: ({
        entries,
        observer
      }) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          setHasPreviouslyIntersected(true);
          reportViewed.current = true;
          reportingLogger.measureTiming('timeBeforeLoad');
        }
      }
    });
  }
  return /*#__PURE__*/_jsx(PlatformResolveComponent, Object.assign({
    "data-test-id": "platform-report-resolver-lazy-load-bypass",
    report: report
  }, rest));
}
export default function PlatformReportResolver(_ref3) {
  let {
      userInfo,
      fiscalYearMonthName,
      report
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const PlatformResolveComponent = useCallback(props => /*#__PURE__*/_jsx(ErrorBoundary, {
    children: /*#__PURE__*/_jsx(UserInfoProvider, {
      userInfo: userInfo,
      children: /*#__PURE__*/_jsx(ReportingLoggerProvider, {
        children: /*#__PURE__*/_jsx(FiscalYearProvider, {
          fiscalYearMonthName: fiscalYearMonthName,
          report: report,
          children: /*#__PURE__*/_jsx(RenderPlatformReportResolveWithPlugins, Object.assign({}, props))
        })
      })
    })
  }), []);
  const PlatformResolveComponentWithGates = useCallback(props => /*#__PURE__*/_jsx(RenderMaybeWithLazyLoad, Object.assign({
    PlatformResolveComponent: PlatformResolveComponent
  }, props)), []);
  return /*#__PURE__*/_jsx(PlatformResolveComponentWithGates, Object.assign({
    report: report
  }, rest));
}
export const __TESTABLE__ = {
  RenderPlatformReportResolve,
  RenderMaybeWithLazyLoad
};